import type { Cybo, CyboReadiness } from "./types.js";

// Payload of `cyborg:cybo_updated_broadcast` (#640): the editable display fields
// of a cybo after an edit, broadcast to every workspace member so rosters stop
// going stale until a reload. Mirrors the relay's broadcast shape.
export interface CyboUpdateBroadcast {
  id: string;
  slug: string;
  // Display fields: absent = unchanged, null = cleared (an agent's update_agent sends only changes).
  name?: string;
  description?: string | null;
  avatar?: string | null;
  role?: string | null;
  provider?: string;
  model?: string | null;
  // #636: editing the provider can flip readiness — carry it so the banner
  // updates live alongside the other display fields. Absent on older relays.
  readiness?: CyboReadiness;
  // The cybo's capability grants AFTER the write. NOT a display field, and that
  // is exactly why it belongs here: the agent-profile Tools card and the editor's
  // Tools step BOTH read `toolGrants` off the roster row (cyboState.list), and
  // nothing else ever refreshes it. Without this key a grant added server-side —
  // by the one-tap integration card, or by another admin's save — stays invisible
  // until a cold reload, and a REVOKED one keeps rendering as still granted. On a
  // screen whose whole job is "what can this agent touch", both directions are a
  // lie. Optional: an older relay omits it, and absent must read as "unchanged",
  // never as "cleared" — hence the presence check in applyCyboUpdate.
  toolGrants?: Cybo["toolGrants"];
  bypassPermissions?: boolean | null;
  // The POST-write grants and autonomy (C2), so a toggle flipped in one tab or by the one-tap card
  // shows everywhere without a reload. Absent on an older relay = unchanged.
  platformPermissions?: string[];
  autonomyLevel?: string | null;
  // Who the agent belongs to after the write (a move can hand it to the new machine's owner), so
  // "My agents" updates live. Optional: an older relay omits it, and absent means unchanged.
  createdBy?: string | null;
  // The CEO seat after the write. Applied by applyCeoSeat (it touches sibling rows), never by
  // applyCyboUpdate. Absent on an older relay = unchanged.
  isDefault?: boolean;
}

/**
 * Apply an edit broadcast onto a cybo IN PLACE (#640). Mutates the target's
 * display fields directly so, when `target` is an element of a Svelte 5 `$state`
 * roster proxy, reactivity is surgical — only the changed keys re-trigger,
 * leaving the array reference and sibling cybos untouched. Non-broadcast fields
 * (soul / isDefault / createdAt / id / slug) are preserved.
 */
export function applyCyboUpdate(target: Cybo, u: CyboUpdateBroadcast): void {
  // A partial broadcast (an agent's update_agent sends only the changed fields) leaves the rest as
  // they are: absent = unchanged, null = cleared. Copying an absent provider blanked an outside bot's
  // kind until reload (New schedule went back to disabled).
  if (u.name !== undefined) target.name = u.name;
  if (u.description !== undefined) target.description = u.description;
  if (u.avatar !== undefined) target.avatar = u.avatar;
  if (u.role !== undefined) target.role = u.role;
  if (u.provider !== undefined) target.provider = u.provider;
  if (u.model !== undefined) target.model = u.model;
  if (u.readiness !== undefined) target.readiness = u.readiness;
  if (u.toolGrants !== undefined) target.toolGrants = u.toolGrants;
  if (u.createdBy !== undefined) target.createdBy = u.createdBy;
  if (u.bypassPermissions !== undefined)
    target.bypassPermissions = u.bypassPermissions ?? undefined;
  if (u.platformPermissions !== undefined) target.platformPermissions = u.platformPermissions;
  if (u.autonomyLevel !== undefined) target.autonomyLevel = u.autonomyLevel;
}

// update_cybo fields that land on the roster row 1:1, with nothing the server derives from them,
// so a click can show them at once. provider / homeDaemonId / isDefault / soul are not here:
// readiness, the CEO seat and the soul excerpt come back from the server, so those writes still
// wait on the roster read (needsRosterRead).
const INSTANT_KEYS = [
  "name",
  "description",
  "avatar",
  "role",
  "model",
  "llmAuthMode",
  "behaviorMode",
  "autonomyLevel",
  "monthlySpendCap",
  "platformPermissions",
  "toolGrants",
  "mcpServers",
  "bypassPermissions",
] as const;

export const needsRosterRead = (fields: object): boolean =>
  ["provider", "homeDaemonId", "isDefault", "soul"].some((k) => k in fields);

/**
 * Show an update_cybo write on the roster row before the relay answers. Returns the undo for a
 * refused write: it puts back only the keys still showing this write, so a newer click on the same
 * key is never reverted by an older refusal.
 */
export function patchCyboNow(target: Cybo, fields: object): () => void {
  const row = target as unknown as Record<string, unknown>;
  const f = fields as Record<string, unknown>;
  const before: Record<string, unknown> = {};
  const shown: Record<string, unknown> = {};
  for (const k of INSTANT_KEYS) {
    if (!(k in f)) continue;
    before[k] = row[k];
    row[k] = f[k];
    shown[k] = row[k]; // read back: a $state row hands out its own proxy for arrays/objects
  }
  return () => {
    for (const k of Object.keys(before)) if (row[k] === shown[k]) row[k] = before[k];
  };
}

// One CEO per workspace: the relay swaps the seat atomically but broadcasts only the edited
// agent, so a `true` here clears every other roster row.
export function applyCeoSeat(list: Cybo[], u: { id: string; isDefault?: boolean }): void {
  if (u.isDefault === undefined) return;
  for (const c of list) {
    if (c.id === u.id) c.isDefault = u.isDefault;
    else if (u.isDefault && c.isDefault) c.isDefault = false;
  }
}

// A session-list row that carries its OWN denormalized copy of the cybo's
// name/avatar (live `Agent` rows + `ArchivedSession` rows). The server stamps
// these when the row is created, and `resolveCyboChatIdentity` prefers this
// denorm over the live roster (it's resolvable from frame 0). So after an
// avatar/name edit the roster refresh alone isn't enough — every session
// surface (sidebar / DM list / agents roster / the agent's own header) keeps
// reading the pre-edit denorm and shows the OLD photo until a cold reload
// re-fetches the rows. Re-stamping these copies is what a restart did for free.
export interface CyboDenormRow {
  cyboId?: string | null;
  cyboName?: string | null;
  cyboAvatar?: string | null;
}

/**
 * Re-stamp the denormalized cybo name/avatar on every session row bound to
 * `cyboId`, IN PLACE (so a Svelte 5 `$state` list proxy reacts surgically —
 * same principle as applyCyboUpdate on the roster element). No-op for rows
 * bound to other cybos. `avatar: null` clears the photo (explicit removal).
 */
export function applyCyboDenormToSessions<T extends CyboDenormRow>(
  rows: T[],
  cyboId: string,
  fields: { name: string; avatar: string | null },
): void {
  for (const row of rows) {
    if (row.cyboId !== cyboId) continue;
    row.cyboName = fields.name;
    row.cyboAvatar = fields.avatar;
  }
}
